import { Heart, ListChecks, Syringe, HeartPulse, Droplets, CalendarDays } from 'lucide-react';
import { benTaubOr, BB_DOC } from './benTaubOr';

/*
 * Ben Taub Cardiac rotation. Built from the Blackboard Cardiac Orientation
 * (orientation guide, attending drip preferences, resident guide and CPB
 * checklists) and Cardiac Protocols (TAVR, ANH). See docs/blackboard-ben-taub-subspecialties.md.
 * Contacts and Locations extend the General OR rotation.
 */

export const benTaubCardiac = {
  accent: benTaubOr.accent,
  icon: Heart,
  tagline: 'CPB checklists, room setup, TAVR, ANH',
  notesKey: 'notes:ben-taub:cardiac',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'cpb', title: 'Bypass Checklist', subtitle: 'Timeout, going on, on pump, weaning, after', icon: ListChecks, action: 'guide:cpb', featured: true },
      { id: 'setup', title: 'Room Setup', subtitle: 'Syringes, drips, lines, by attending', icon: Syringe, action: 'guide:setup' },
      { id: 'tavr', title: 'TAVR', subtitle: 'OR 1 setup, pacing, heparin, protamine', icon: HeartPulse, action: 'guide:tavr' },
      { id: 'anh', title: 'Hemodilution (ANH)', subtitle: 'Who qualifies, how much, reinfusion', icon: Droplets, action: 'guide:anh' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'First day, typical day, habits', icon: CalendarDays, action: 'guide:day' },
    ] },
  ],

  locations: {
    ...benTaubOr.locations,
    groups: [
      { id: 'cardiac', title: 'Cardiac rooms', items: [
        { name: 'Cardiac OR', floor: '2nd floor', where: 'OR 5', note: 'Drugs from the Pyxis' },
        { name: 'TAVR', floor: '2nd floor', where: 'OR 1', note: 'C-arm on the left, IV poles on the right' },
      ] },
      ...benTaubOr.locations.groups,
    ],
  },

  documents: {
    storageKey: 'ben-taub-cardiac-pinned-docs',
    groups: [
      { id: 'cardiac', title: 'Cardiac', items: [
        { title: 'Cardiac orientation', subtitle: 'Orientation guide, drip preferences, resident checklists. Blackboard', url: BB_DOC('_255066_1') },
        { title: 'Cardiac protocols', subtitle: 'TAVR, ANH, ERAS cardiac and thoracic. Blackboard', url: BB_DOC('_256277_1') },
      ] },
      ...benTaubOr.documents.groups,
    ],
  },

  guides: {
    cpb: {
      title: 'Bypass Checklist',
      segments: [
        { id: 'timeout', label: 'Timeout', sections: [
          { title: 'Pre-induction timeout, read by the resident', blocks: [{ type: 'bullets', items: [
            'Antibiotics: weight-based vancomycin and cefazolin unless contraindicated or allergic',
            'Allergies',
            'Date and time of the last beta blocker, every CABG',
            'Blood conservation: retrograde autologous prime, ANH, or both',
            'Postop plan: regional block for fast-track, ICU, intubated and sedated',
            'Concerns',
          ] }] },
        ] },
        { id: 'on', label: 'Going on', sections: [
          { title: 'Heparin and cannulation', blocks: [{ type: 'bullets', items: [
            'Heparin only when the surgeon asks: 300–400 U/kg',
            'Call the attending when heparin is requested if they are not in the room',
            'ACT above 480 (i-STAT above 400) before bypass',
            'Note urine output',
            'Systolic under 100 for cannulation',
            'Retrograde autologous prime may need a vasoactive. Never without the attending',
            'Confirm therapeutic heparin before going on',
          ] }] },
          { title: 'Immediately on bypass', blocks: [{ type: 'bullets', items: [
            'Arterial inflow cannula bright red',
            'Adequate flow and MAP',
            'Ventilator and volatile off',
            'Alarms and pulse-ox tone adjusted',
            'Watch the head and face, CVP and cerebral oximetry for SVC drainage',
            'Anesthesia per the attending: dexmedetomidine, ketamine, opioid, benzodiazepine, relaxant',
            'Urine output to perfusion every 30–60 min',
            'Inotropes usually off. Vasoactives to the MAP goal. Every change confirmed with the attending',
            'Chart bypass on, cross-clamp on and off, circulatory arrest, defibrillation',
          ] }] },
        ] },
        { id: 'pump', label: 'On pump', sections: [
          { title: 'During bypass', blocks: [{ type: 'bullets', items: [
            { text: 'Malperfusion signs (cerebral oximetry, urine output, acid-base, SvO2): attending and perfusionist immediately', warn: true },
            'ABG every 30–60 min',
            'Pump cardiac index above 2.2–2.4',
            'Nadir hematocrit usually above 20–21%',
            'Plan post-bypass products. TEG on pump: ask',
            'Plan hemodynamic support for separation',
            'Nitroglycerin during rewarming per the attending',
            'Protamine 1 mg per 100 U heparin, labelled, on the back table. Not connected until the surgeon asks for a test dose',
          ] }] },
        ] },
        { id: 'wean', label: 'Weaning', sections: [
          { title: 'WRMVP', blocks: [{ type: 'rows', rows: [
            { label: 'Warm', value: 'Core or blood above 36–36.5 °C' },
            { label: 'Rhythm', value: 'Pacing? AV conduction?' },
            { label: 'Monitors', value: 'Tone and alarms back on' },
            { label: 'Ventilation, volatile', value: 'Back on. Mild respiratory alkalosis' },
            { label: 'Perfusion, potassium', value: 'Vasoactives running. K above 4, usually about 5. Not acidotic' },
          ] }] },
        ] },
        { id: 'after', label: 'After', sections: [
          { title: 'Protamine', blocks: [{ type: 'steps', items: [
            'Only when the surgeon asks. Never hang it early, never push it, run it slowly',
            '10 mg test dose, announced. Perfusionist and surgeon acknowledge',
            'Watch for hypotension, anaphylaxis, pulmonary hypertension',
            'Announce one third, one half, full dose',
            'Labs 3 min after the full dose: ABG, ACT twice, PT/INR/PTT, fibrinogen, platelets, TEG 6 (2 blue, 1 purple)',
          ] }] },
          { title: 'Coming off and transfer', blocks: [{ type: 'bullets', items: [
            'Any uptitration of support: alert the attending. TEE probe stays in',
            'Products 30–45 min ahead of need. Tell the attending',
            'ABG every 30–45 min. Bicarbonate is temporising',
            'Potassium above 4',
            'Backup heparin vial in case of urgent re-bypass',
            'Hypotension on transfer is common: volume and vasoactives ready. Do not move if unstable. TEE stays until the final exam',
          ] }] },
        ] },
      ],
      sources: [{ title: 'Cardiac resident guide and checklists', href: BB_DOC('_255066_1'), detail: 'Blackboard, sign-in required' }],
    },

    setup: {
      title: 'Room Setup',
      segments: [
        { id: 'syringes', label: 'Syringes', sections: [
          { title: 'Pressors and vasodilators', blocks: [{ type: 'rows', rows: [
            { label: 'Epinephrine', value: '10 mcg/mL' },
            { label: 'Norepinephrine', value: '10 mcg/mL' },
            { label: 'Ephedrine', value: '5 mg/mL' },
            { label: 'Phenylephrine', value: '100 mcg/mL' },
            { label: 'Calcium chloride', value: '100 mg/mL' },
            { label: 'Esmolol', value: '10 mg/mL' },
            { label: 'Nitroglycerin', value: '100 mcg/mL' },
          ] }] },
          { title: 'Induction and maintenance', blocks: [{ type: 'rows', rows: [
            { label: 'Etomidate or propofol', value: '2 mg/mL or 10 mg/mL', note: 'Ask the attending' },
            { label: 'Midazolam', value: '1 mg/mL, 2 mL' },
            { label: 'Fentanyl', value: '50 mcg/mL, 5 mL' },
            { label: 'Lidocaine', value: '20 mg/mL, two' },
            { label: 'Rocuronium', value: '10 mg/mL' },
            { label: 'Succinylcholine', value: '20 mg/mL' },
          ] }] },
          { title: 'Also drawn', confirm: true, blocks: [{ type: 'rows', rows: [
            { label: 'Heparin', value: '300 U/kg' },
            { label: 'Aminocaproic acid', value: '10 g' },
            { label: 'Magnesium', value: '2 g', note: 'Guide reads 2 mg/mL, 2 mL' },
          ] }] },
        ] },
        { id: 'drips', label: 'Drips', sections: [
          { title: 'Standard', blocks: [{ type: 'rows', rows: [
            { label: 'Norepinephrine', value: '4 mg in 250 mL', note: '16 mcg/mL' },
            { label: 'Epinephrine', value: '4 mg in 250 mL' },
            { label: 'Carrier', value: '500 mL NS + 10 g aminocaproic acid' },
          ] }, { type: 'note', text: 'Milrinone if the EF is low. Insulin if diabetic. Anything else per the attending sheet. Ask the day before.' }] },
        ] },
        { id: 'lines', label: 'Lines', sections: [
          { title: 'Every patient', blocks: [{ type: 'bullets', items: [
            'Arterial line and one good peripheral IV in preop. Type and screen first so the blood bank is not the delay',
            '9 Fr MAC and a PA catheter',
            'Two ECG sets, one for the IABP',
            'Defibrillator pads',
            'IABP patients: arterial line placed in the ICU, straight to the OR. Grab the perfusionist to help',
            'ACT, with or without an ABG, after heparin and before bypass',
            'Consent everyone for regional. A block may follow the case',
          ] }] },
        ] },
        { id: 'attendings', label: 'By attending', sections: [
          { title: 'Drip preferences', blocks: [{ type: 'table', cols: ['Attending', 'Adds'], rows: [
            ['Everyone', 'Aminocaproic acid. Epinephrine and norepinephrine drips. Milrinone if low EF. Insulin if diabetic'],
            ['Best', 'Dexmedetomidine. Ketamine 200 mg/100 mL if ERAS'],
            ['Deng', 'Dexmedetomidine if ERAS'],
            ['Duong', 'Dexmedetomidine'],
            ['Ibekwe', 'Dexmedetomidine. Ketamine if ERAS'],
            ['Lei', 'Dexmedetomidine'],
            ['Nguyen', 'Vasopressin. Dexmedetomidine. Ketamine if ERAS'],
            ['Nikolaidis', 'Vasopressin. Cisatracurium'],
            ['Palvadi', 'Vasopressin. Cisatracurium. Milrinone always'],
            ['Wei', 'Dexmedetomidine'],
          ] }, { type: 'note', text: 'A guide only. Ask the day before.' }] },
        ] },
      ],
      sources: [{ title: 'Cardiac orientation and drip preferences', href: BB_DOC('_255066_1'), detail: 'Blackboard, sign-in required' }],
    },

    tavr: {
      title: 'TAVR',
      segments: [
        { id: 'room', label: 'Room', sections: [
          { title: 'OR 1 setup', blocks: [{ type: 'bullets', items: [
            'MSMAIDS. McGrath and a sized tube',
            'Radiolucent ECG cable (blue) and leads. Defibrillator pads',
            'Extensions on the circuit, Hotline and arterial line (high-pressure). The table moves away',
            '8-channel Alaris',
            'All IV poles on the right. The C-arm works from the left',
            'Philips echo with a TTE probe. TEE available for conversion',
            'i-STAT ACT cartridges',
            'Underbody forced-air blanket, Bair Hugger at the foot',
            'Lead aprons',
            'At least 2 units PRBC in the room',
          ] }] },
        ] },
        { id: 'drugs', label: 'Drugs', sections: [
          { title: 'Drips, discuss with the attending', blocks: [{ type: 'rows', rows: [
            { label: 'Carrier', value: 'NS 500 mL at 50 mL/h' },
            { label: 'Epinephrine', value: '4 mg/250 mL at 2 mcg/min' },
            { label: 'Norepinephrine', value: '4 mg/250 mL at 2 mcg/min' },
            { label: 'Nitroglycerin', value: '50 mg/250 mL at 5 mcg/min', note: '200 mcg/mL' },
            { label: 'Dexmedetomidine', value: '200 mcg/50 mL at 0.3 mcg/kg/h' },
            { label: 'Insulin', value: '100 U/100 mL at 1 U/h', note: 'If diabetic' },
          ] }] },
          { title: 'Syringes', blocks: [{ type: 'rows', rows: [
            { label: 'Etomidate', value: '2 mg/mL' },
            { label: 'Lidocaine', value: '20 mg/mL' },
            { label: 'Fentanyl', value: '50 mcg/mL, 250 mcg' },
            { label: 'Midazolam', value: '1 mg/mL, 2 mg' },
            { label: 'Paralytic', value: 'Per patient' },
            { label: 'Phenylephrine', value: '100 mcg/mL' },
            { label: 'Ephedrine', value: '5 mg/mL' },
            { label: 'Epinephrine', value: '10 mcg/mL' },
            { label: 'Norepinephrine', value: '10 mcg/mL' },
            { label: 'Calcium chloride', value: '100 mg/mL' },
            { label: 'Nitroglycerin', value: '100 and 25 mcg/mL' },
            { label: 'Esmolol', value: '10 mg/mL' },
            { label: 'Heparin', value: '1000 U/mL', note: 'About 100 U/kg, ACT goal above 300' },
          ] }, { type: 'note', text: 'Bypass-dose heparin in the room, not drawn (ACT above 480 if converted). Protamine stays in the Pyxis. Antibiotics: vancomycin 1 g and cefazolin 2 g.' }] },
        ] },
        { id: 'preop', label: 'Preop', sections: [
          { title: 'Before the room', blocks: [{ type: 'bullets', items: [
            'Consent and full evaluation',
            '2 units PRBC crossmatched by the interventional team. Check',
            'Two large-bore IVs, 16–18 g',
            'Radial arterial line before induction, on the arm opposite any planned radial access. Check pigtail return',
            'Baseline ABG and ACT',
          ] }] },
        ] },
        { id: 'intraop', label: 'Intraop', sections: [
          { title: 'Setup and induction', blocks: [{ type: 'bullets', items: [
            'Pads side to side; the chest is prepped for emergent sternotomy. Tegaderm over leads and pads',
            'Timeout',
            'Sedate with care: severe aortic stenosis, poor ventricular function',
            'TTE images to the central screen (DisplayPort-to-DVI adapter from the circulator)',
            'Extensions on everything. Right arm tucked, left arm in the field',
            'Femoral pacing wires and the pacing box come from the interventional team',
          ] }] },
          { title: 'Heparin and deployment', blocks: [{ type: 'bullets', items: [
            'Heparin on request once the 14–16 Fr sheath is in. ACT at 3 min, then every 30 min. Confirm ACT above 300 before deployment',
            'Rapid pacing around 180 for valvuloplasty and deployment: profound drop in output. Support the MAP after. Lidocaine 1 mg/kg for ectopy',
            'Sapien 3 (balloon-expandable): ventilator hold and rapid pacing. Evolut (self-expanding): optional',
            'Sonographer and structural imaging cardiologist in the room before deployment',
            'Protamine about 1 mg/kg after the sheath is out; may be withheld if a stent was placed. ACT after',
            'Hypertension after the new valve: nitroglycerin ready',
          ] }] },
        ] },
        { id: 'postop', label: 'Postop', sections: [
          { title: 'To the CCU', blocks: [{ type: 'bullets', items: [
            'Bed, requirements, screening swab, report',
            'Discuss leaving the pacing wires: heart block can be delayed with self-expanding valves',
            'Full monitors for transport',
          ] }] },
        ] },
      ],
      sources: [{ title: 'Ben Taub CV TAVR protocol', href: BB_DOC('_256277_1'), detail: 'Blackboard, sign-in required' }],
    },

    anh: {
      title: 'Acute Normovolemic Hemodilution',
      sections: [
        { title: 'Who', blocks: [{ type: 'bullets', items: [
          'Cardiac surgery with expected blood loss above 750 mL–1 L',
          'No extra consent',
        ] }] },
        { title: 'Not if', blocks: [{ type: 'bullets', items: [
          'Hemoglobin under 11',
          'Left main disease, or severe or critical aortic stenosis',
          'Creatinine clearance under 60 or creatinine above 1.5',
          'EF under 30% or unstable',
          'Carotid stenosis above 60%, or stroke within 3 weeks',
          'More than moderate COPD or FEV1 under 50%',
          'Significant liver disease or AST/ALT above twice normal',
          'INR above 1.5 or platelets under 100',
          'Clotting or hemoglobin disorders: vWD, DIC, sickle cell, G6PD, spherocytosis, PNH',
          'Sepsis',
        ] }] },
        { title: 'How much', blocks: [{ type: 'table', cols: ['Build', 'Male', 'Female'], rows: [
          ['Muscular', '75 mL/kg', '70 mL/kg'],
          ['Average', '70 mL/kg', '65 mL/kg'],
          ['Thin', '65 mL/kg', '60 mL/kg'],
          ['Obese', '60 mL/kg', '55 mL/kg'],
        ] }, { type: 'note', text: 'Blood volume from ideal body weight. Volume to remove = blood volume × (Hgb − 10) ÷ ((Hgb + 10) ÷ 2). Weigh the bags: 1 mL is 1.06 g.' }] },
        { title: 'Before bypass', blocks: [{ type: 'steps', items: [
          'Once draped, draw from the 9 Fr MAC or the arterial line with an extra extension, 14–16 g needle into a sterile cap, aseptic',
          'Each bag under 477 g (450 mL). Tie off, label with the sticker and bag number, agitate, keep at room temperature',
          'Replace 1:1 with albumin as you draw',
          'Stop for ischemia (ST changes, new block, QRS widening) or hypotension that does not respond',
        ] }] },
        { title: 'On bypass', blocks: [{ type: 'bullets', items: [
          'Usual heparin, 300 U/kg',
          'Hemoglobin under 7 near the end and perfusion cannot concentrate: give the stored blood, most recent bag first',
          'MAP 60–80',
        ] }] },
        { title: 'After bypass', blocks: [{ type: 'bullets', items: [
          'Post-protamine ACT first, then reinfuse the stored whole blood, most recent bag first, before cell saver',
          'Coagulopathy with FFP and platelets as usual',
          'PT/PTT/INR, platelets and TEG after reinfusion',
        ] }] },
      ],
      sources: [{ title: 'BT Cardiac ANH protocol', href: BB_DOC('_256277_1'), detail: 'September 2020. Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'First day', blocks: [{ type: 'bullets', items: [
          'In by 06:00 or earlier. The setup is big',
          '2nd floor OR 5. TAVR in OR 1',
          'Drugs from the Pyxis',
          'Inpatient cardiac cases are consented the day before',
        ] }] },
        { title: 'Typical day', blocks: [{ type: 'bullets', items: [
          'In 05:45–06:00',
          'Arterial line and one good IV in preop. Type and screen first',
          'IABP patients come straight from the ICU with the arterial line already in',
          'The end of the day varies with the case',
        ] }] },
        { title: 'Before you call the attending', blocks: [{ type: 'bullets', items: [
          'Preop the day before',
          'Review the TTE, cath, carotid ultrasound and PFTs',
          'CA-1 and CA-3 agree on a plan first, or discuss as a group',
          'Outpatients who are not anemic may get ANH. Read the protocol',
          'Drip preferences from the attending sheet',
        ] }] },
        { title: 'Habits', blocks: [{ type: 'bullets', items: [
          'Transport ICU patients yourself',
          'Discuss FFP and platelets with the blood bank while rewarming',
          'Protamine: never hung until told, never pushed, run slowly',
        ] }] },
      ],
      sources: [{ title: 'Cardiac orientation guide', href: BB_DOC('_255066_1'), detail: 'Blackboard, sign-in required' }],
    },
  },
};
